import { cn, Loader, RiFileAddLine, Stack } from 'component-library';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { Header } from '@/components/widgets/a/Text/Text';
import { useGenerateReportByType } from '@/pages/shared/hooks/useReports';

type AvailableReportTypes = (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];

type AvailableReportCardProps = {
  report: AvailableReportTypes;
  className?: string;
};

export const AvailableReportCard = ({ report, className }: AvailableReportCardProps) => {
  const { mutate: generateReport, isPending } = useGenerateReportByType({
    reportType: report,
  });

  const { t } = useTranslation();
  const reportTypeLabel = t(`reports.reportTypes.${report}`);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    generateReport();
  };

  return (
    <Stack
      data-testid='available-report-card'
      className={cn(
        'rounded-3xl border border-neutral-black-12 p-6 transition-all duration-200 gap-2',
        'w-[380px] h-[320px]',
        isPending === true
          ? 'hover:cursor-not-allowed'
          : 'hover:cursor-pointer hover:border-primary-hover hover:bg-neutral-hover',
        className,
      )}
      center
      onClick={handleClick}
      role='button'
    >
      <Stack data-testid='available-report-card-thumbnail' className='h-32' center centerMain>
        <Stack className='text-neutral-black-40 text-center'>
          {isPending ? <Loader /> : <RiFileAddLine className='w-16 h-16 mx-auto mb-2' />}
        </Stack>
      </Stack>
      <Stack data-testid='available-report-card-text' spacing={2}>
        <Header>
          <span className='line-clamp-2 text-neutral-black-80'>
            {t('reports.generateReport', {
              report: reportTypeLabel,
            })}
          </span>
        </Header>
      </Stack>
    </Stack>
  );
};
